import { CalculatedMeasureColumn, MeasureColumn } from '@sisense/sdk-data';
import {
	ChartType,
	ChartWidgetProps,
	StyledColumn,
	StyledMeasureColumn,
	WithCommonWidgetProps,
} from '@sisense/sdk-ui';
import { HttpClient } from '@sisense/sdk-rest-client';
import OpenAI from 'openai';
import { z } from 'zod';
import { QueryResult } from '../build-query/types';
import { ChartState, Dataset, QueryState } from '@/services/nlq/nlq-service';

// Simple artifact reference type (no dependency on assistant types)
export type ArtifactReference = {
	artifactId?: string;
};

export type ExtendedChartWidgetProps = WithCommonWidgetProps<
	ChartWidgetProps,
	'chart'
> &
	ArtifactReference & {
		/**
		 * data columns used to generate chart recommendations
		 */
		dataColumns?: ColumnType[];
		/** Query state used to generate this chart — needed to revise query structure incrementally */
		queryState?: QueryState;
		/** Sample / tabular payload from NLQ when available (same source as standalone buildQuery). */
		dataset?: Dataset;
		/** Optional CSDK chart JSON when a caller persists it alongside NLQ state (distinct from REST `chartState` / {@link QueryState}). */
		chartState?: ChartState;
	};

export type NullableChartType = ChartType | null | undefined;

/**
 * Compact CSDK chart configuration for LLM consumption.
 * Alias for {@link ChartState} — the serialized form produced by `translateChartToJSON`.
 */
export type BaseChartSummary = ChartState;

/**
 * Summary of a chart for LLM consumption and asset extraction
 */
export interface ChartSummary {
	/** Status message indicating success or failure */
	message: string;
	/** Date and time ISO string of when the chart was created */
	createdAt: string;
	/** Unique identifier for the chart (typically the tool call ID) */
	chartId: string;
	/** Data source the chart was built from */
	dataSourceTitle: string;
	/** Compact CSDK chart configuration (chartType, dataOptions, filters, styleOptions) */
	chartState?: BaseChartSummary;
	/** NLQ state for incremental structural revisions via updateChartQuery */
	queryState?: QueryState;
	/** Whether this chart creation resulted in an error */
	isError?: boolean;
	/** Dataset rows/sample from NLQ when present on the saved chart widget; surfaced on {@link ChartSummary} when `naturalResponseEnabled` */
	dataset?: Dataset;
}

/**
 * Row in the `charts` array of the build-chart system prompt: either a full
 * {@link ChartSummary} from the host or a single targeted entry when revising by id.
 */
export type ChartContextPromptEntry =
	| ChartSummary
	| { chartId: string; chartState: BaseChartSummary };

export type ColumnType =
	| StyledColumn
	| StyledMeasureColumn
	| Column
	| MeasureColumn
	| CalculatedMeasureColumn;

export interface Column {
	id?: number;
	name: string;
	type?: string;
}

const cartesianChartType = z.enum(['bar', 'column', 'area', 'line', 'polar']);

const categoricalChartType = z.enum(['pie', 'funnel', 'treemap', 'sunburst']);

const otherChartType = z.enum([
	'scatter',
	'table',
	'indicator',
	'boxplot',
	'areamap',
	'scattermap',
	'arearange',
]);

export const chartType = z.enum([
	...cartesianChartType.options,
	...categoricalChartType.options,
	...otherChartType.options,
]);

const cartesianChartSchema = z
	.object({
		type: cartesianChartType,
		category: z.array(z.number()),
		value: z.array(z.number()),
		breakBy: z.array(z.number()),
	})
	.strict();

const categoricalChartSchema = z
	.object({
		type: categoricalChartType,
		category: z.array(z.number()),
		value: z.array(z.number()),
	})
	.strict();

const scatterChartSchema = z
	.object({
		type: z.literal('scatter'),
		x: z.array(z.number()),
		y: z.array(z.number()),
		breakByPoint: z.array(z.number()),
		breakByColor: z.array(z.number()),
		size: z.array(z.number()),
		// showLegend: z.boolean(),
	})
	.strict();

const tableChartSchema = z
	.object({
		type: z.literal('table'),
		columns: z.array(z.number()),
		pagination: z.boolean(),
		rowsPerPage: z.number(),
	})
	.strict();

const rangeChartSchema = z
	.object({
		type: z.literal('range'),
		category: z.array(z.number()),
		value: z.array(z.number()),
		breakBy: z.array(z.number()),
	})
	.strict();

const indicatorChartSchema = z
	.object({
		type: z.literal('indicator'),
		value: z.array(z.number()),
		secondary: z.array(z.number()),
		min: z.array(z.number()),
		max: z.array(z.number()),
	})
	.strict();

const boxplotChartSchema = z
	.object({
		type: z.literal('boxplot'),
		category: z.array(z.number()),
		value: z.array(z.number()),
		boxType: z.enum(['iqr', 'extremums', 'standardDeviation']),
		outliersEnabled: z.boolean(),
	})
	.strict();

const areamapChartSchema = z
	.object({
		type: z.literal('areamap'),
		geo: z.array(z.number()),
		color: z.array(z.number()),
	})
	.strict();

const scattermapChartSchema = z
	.object({
		type: z.literal('scattermap'),
		geo: z.array(z.number()),
		size: z.array(z.number()),
		colorBy: z.array(z.number()),
		details: z.array(z.number()),
		locationLevel: z.enum(['auto', 'country', 'state', 'city']),
	})
	.strict();

export const axesMapping = z.discriminatedUnion('type', [
	cartesianChartSchema,
	categoricalChartSchema,
	scatterChartSchema,
	tableChartSchema,
	rangeChartSchema,
	indicatorChartSchema,
	boxplotChartSchema,
	areamapChartSchema,
	scattermapChartSchema,
]);

export const nullableChartType = chartType
	.nullable()
	.describe(
		'If the user prompt does not specify a known chart type, pass null; otherwise, pass the specified chart type',
	);

export const chartIdSchema = z
	.string()
	.describe(
		'The ID of the chart to update. If not provided, use value "chart_id_placeholder"',
	)
	.default('chart_id_placeholder');

export const queryIdSchema = z
	.string()
	.nullable()
	.default(null)
	.describe(
		'The ID of the build query to use. This ID is created by the build query tool.',
	);

export const chartRecommendationsSchema = z.object({
	chartType: chartType,
	axesMapping: axesMapping,
});

/**
 * Canonical tool description for buildChart — reusable by any host (sdk-assistant, MCP server, custom integrations).
 * Hosts may append host-specific notes but should not duplicate or contradict this text.
 */
export const buildChartToolDescription = `Creates or revises a chart from a natural language prompt.

Three ways to scope the run (pass \`userPrompt\` unchanged in all cases):

1. **New chart from scratch:** omit both \`priorChartId\` and \`queryId\` and describe the desired chart in \`userPrompt\`.
2. **Revise an existing chart:** pass \`priorChartId\` (the chartId from a prior buildChart result, or the widget id when inside a dashboard). The chart's existing data context and style are preserved across revisions. Do not run other tools (e.g. query tool) when revising an existing chart.
3. **Verbatim prior query:** pass \`queryId\` (an id from a prior data query result) to chart to reuse that query result if needed to not waste the prior query work.

Do not pass both \`priorChartId\` and \`queryId\` — if both are set, \`priorChartId\` takes precedence.

Pass the user prompt exactly as received — do not transform or modify it.`;

const priorChartIdField = z
	.string()
	.optional()
	.describe(
		'Id of the chart in scope (e.g. chartId from a prior buildChart). Pass when revising that chart; ' +
			'omit for a new chart. The tool routes the prompt to the appropriate updates.',
	);

export const buildChartSchema = z.object({
	dataSourceTitle: z.string().describe('Data source title'),
	userPrompt: z
		.string()
		.describe(
			'Combined natural language user prompt describing multiple actions on a chart. Pass the user prompt exactly as received - do NOT transform or modify it. The build chart logic will handle prompt splitting internally.',
		),
	priorChartId: priorChartIdField,
	queryId: z
		.string()
		.nullable()
		.optional()
		.describe(
			'When the chart is based on a prior query result, pass its queryId; otherwise omit or pass null.',
		),
});

const baseChartSchema = z.object({
	userPrompt: z
		.string()
		.describe(
			'Natural language user prompt describing a single action on a chart',
		),
});

export const createChartSchema = z.object({
	queryPrompt: z
		.string()
		.describe(
			'Data-focused query prompt for retrieving data. Be faithful to the original user prompt but remove chart-specific instructions (e.g., "bar chart", "use X as dimension"). Focus on the data intent - the system will extract specific dimensions, measures, and filters automatically.',
		),
	chartPrompt: z
		.string()
		.describe(
			'Visualization-focused prompt for chart recommendations. Be faithful to the original user prompt and keep chart-specific instructions. Do not infer the chart type from the user prompt.',
		),
	chartType: nullableChartType,
	chartTitle: z.string().describe('The new chart title'),
	queryId: queryIdSchema,
});

export const updateChartSchema = baseChartSchema.extend({
	chartId: chartIdSchema,
});

export const updateChartTitleSchema = updateChartSchema.extend({
	chartTitle: z.string().describe('The new chart title'),
});

export const updateChartTypeSchema = updateChartSchema.extend({
	chartType: chartType,
});

export const updateChartTypeOfMeasuresSchema = updateChartSchema;

export const updateChartSortingSchema = updateChartSchema;

export const updateTrendAndForecastSchema = updateChartSchema;

export const updateOtherChartDataOptionsSchema = updateChartSchema;

export const updateChartStyleOptionsSchema = updateChartSchema;

export const updateChartQuerySchema = z.object({
	revisionPrompt: z
		.string()
		.describe(
			'Natural language description of the structural change to apply ' +
				'(e.g. "filter for USA", "add product breakdown", "swap Revenue for Profit"). Do not repeat the existing data structure in the prompt.',
		),
	chartId: z
		.string()
		.describe(
			'The chartId of the existing chart whose query structure is being revised',
		),
});

/**
 * Build chart context interface
 */
export interface BuildChartContext {
	/**
	 * Tool call ID
	 */
	toolCallId: string;
	/**
	 * Data source title (required)
	 */
	dataSourceTitle: string;
	/** Chart summaries to be used as context */
	chartSummaries?: ChartSummary[];
	/**
	 * Retrieve a chart by its ID
	 * @param chartId - The ID of the chart to retrieve
	 * @returns The chart widget props
	 */
	retrieveChart?: (chartId: string) => ExtendedChartWidgetProps | null;
	/**
	 * Save a chart widget props
	 * @param chartId - The ID of the chart to save
	 * @param chartWidgetProps - The chart widget props to save
	 */
	saveChart?: (
		chartId: string,
		chartWidgetProps: ExtendedChartWidgetProps,
	) => void;
	/**
	 * Retrieve a query result by its ID
	 * @param queryId - The ID of the query to retrieve
	 * @returns The query result, or null if not found
	 */
	retrieveQuery?: (queryId: string) => QueryResult | null;
	/** Whether NLQ V3 is enabled */
	isNlqV3Enabled?: boolean;
	/**
	 * When false, incremental NLQ uses Layer-2 text augmentation instead of REST `chartState`.
	 * Typically threaded from host config when available.
	 */
	isNlqDeltaEnabled?: boolean;
	httpClient?: HttpClient;
	openAIClient?: OpenAI;
	/**
	 * When true, include `dataset` on the returned {@link ChartSummary} when the saved chart
	 * widget carries `dataset`.
	 */
	naturalResponseEnabled?: boolean;
	/** Common filters (e.g., from dashboard filters) to be applied to the query of the chart. */
	commonFilters?: unknown;
}
